<script>
import model from '@/model/company';
import userModel from '@/model/user';

import FormBlock from '@/components/FormBlock.vue';
import UserAvatar from '@/components/UserAvatar.vue';
import EllipsisMenu from '@/components/EllipsisMenu.vue';
import EmptyList from '@/components/ui/EmptyList.vue';
import popupModel from '@/model/popup';
import CompanyDepartmentFormPopupWrappable from '@/components/company/CompanyDepartmentFormPopupWrappable.vue';
import UserSelectPopup2 from '@/components/UserSelectPopup2.vue';
import UserDetailPopup from '@/components/UserDetailPopup.vue';
import CompanyEmployeeForm from '@/components/company/CompanyEmployeeForm.vue';

export default {
  name: 'DepartmentTile',
  components: { CompanyEmployeeForm, UserDetailPopup, UserSelectPopup2, FormBlock, EllipsisMenu, EmptyList, UserAvatar },
  props: {
    department: Object
  },
  computed: {
    currUser() { return this.$store.state.auth.user; },
    company() { return this.$store.getters['company/withMembers']; },
    employees() { return this.$store.state.company.employees ?? []; },
    deputies() { return this.company.deputies ?? [] },
    isOwner() { return this.company?.owner?.id === this.currUser.id },
    isDeputy() { return this.deputies.find(e => e.user.id === this.currUser.id) },
    isHead() { return this.department.members.find(m => m?.employee?.user?.id === this.currUser.id && m.role === 'head') },
  },
  methods: {
    async openUserDetail(member) {
      this.$refs.userDetailPopup.open(member.employee.user);
      this.$nextTick(() => {
        if (member['@id'] === 0) return;

        const initRole = (this.isOwner || this.isDeputy || this.isHead) && member['@type'] !== 'Deputy' ? member.role : null; // this.department['@id'] !== 0
        const initPosition = this.isOwner || this.isDeputy ? (member.employee.position ?? '') : null;
        const onFinish = (role, position) => {
          if (role !== null && role !== initRole) this.$store.dispatch('company/updateMember', { id: member.id, role });
          if (position !== null && position !== initPosition) this.$store.dispatch('company/updateEmployeePosition', { employee: member.employee, position });
          this.$refs.userDetailPopup.close();
        };
        this.$refs.userForm.init(initRole, initPosition, onFinish);
      });
    },
    getDepartmentMenu() {
      if (this.department.id === 0) {
        return this.isOwner || this.isDeputy
          ? [{ text: 'Добавить заместителя', icon: 'add', handler: () => { this.addMember(); } }]
          : [];
      }

      if (!this.isOwner && !this.isDeputy && !this.isHead) return [];

      return [
        { text: 'Добавить участника отдела',  icon: 'add',   handler: () => { this.addMember();         } }, // eslint-disable-line
        { text: 'Редактировать отдел',        icon: 'edit',  handler: () => { this.editDepartment();    } }, // eslint-disable-line
        { text: 'Удалить отдел',              icon: 'trash', handler: () => { this.deleteDepartment();  } }, // eslint-disable-line
      ];
    },
    getMemberMenu(member) {
      const items = [];

      const isHeadOrHigher =  this.isOwner || this.isDeputy || this.isHead;
      if (!isHeadOrHigher) return [];

      if (member['@id'] !== 0) {
        items.push({
          text: 'Исключить из отдела',
          icon: 'trash',
          handler: () => { this.department.id === 0 ? this.deleteDeputy(member, this.department) : this.deleteMember(member, this.department); },
        });

        items.push({
          text: 'Уволить',
          icon: 'trash',
          handler: () => { model.employeeDelete(member.employee) },
        });
      }

      return items;
    },

    async editDepartment() {
      const { id, name } = this.department;
      const data = await popupModel.add({
        wrappable: true,
        component: CompanyDepartmentFormPopupWrappable,
        params: { id, name },
      });

      if (!data) return;

      await this.$store.dispatch('company/updateDepartment', { id, ...data });
      await userModel.fetchSelf();
    },
    async deleteDepartment() {
      const { id, name } = this.department;
      const answer = await this.$confirm(`Вы действительно хотите удалить отдел [${name}]?`);
      if (!answer) return;
      await this.$store.dispatch('company/deleteDepartment', { id });
      await userModel.fetchSelf();
    },

    async addMember() {
      if (this.employees.length === 0) {
        await this.$goto(
          'В отдел компании можно добавить только сотрудников. В Вашей компании пока еще нет сотрудников. Для начала перейдите на страницу компании и добавьте сотрудников компании.',
          { name: 'company-employees', params: { companyId: this.company.id } },
          'Перейти на страницу сотрудников',
        );
        return;
      }

      const department = this.department
      const usersBeforeSelect = department.members.map(m => m.employee['@id']);
      const usersToSelect = this.employees.map(e => ({ ...e.user, '@id': e['@id'] }));
      const selectedUsers = await this.$refs.userSelectPopup.open(usersBeforeSelect, true, usersToSelect, 'Сотрудники компании', 'В компании еще нет сотрудников');

      if (!selectedUsers) return;

      const filtered = selectedUsers
        .filter(e => !usersBeforeSelect.includes(e['@id']))
        .map(employee => ({ department: department['@id'], employee: employee['@id'], role: 'member', }))
      ;

      department.id === 0
        ? await this.$store.dispatch('company/addDeputies', filtered)
        : await this.$store.dispatch('company/addMembers', filtered);
    },
    async deleteMember({ id, employee }) {
      const answer = await this.$confirm(`Вы действительно хотите исключить сотрудника [${employee.user.name} ${employee.user.surname}]?`);
      if (!answer) return;
      await this.$store.dispatch('company/deleteMember', { id });
    },
    async deleteDeputy(member) {
      const { employee } = member;
      const answer = await this.$confirm(`Вы действительно хотите исключить сотрудника [${employee.user.name} ${employee.user.surname}]?`);
      if (!answer) return;
      await this.$store.dispatch('company/deleteDeputy', employee);
    },
  },
}
</script>

<template lang="pug">
  FormBlock.b-department-tile(v-if="department && currUser")
    UserDetailPopup(ref="userDetailPopup")
      CompanyEmployeeForm(ref="userForm")
    UserSelectPopup2(ref="userSelectPopup")
    ._top
      ._name {{ department.name }}
      ._my(v-if="isHead") Мой отдел
      EllipsisMenu._menu(:items="getDepartmentMenu()")
    ._body
      EmptyList._empty(v-if="department.members.length === 0") В этом отделе пока нет сотрудников
      template(v-else)
        .b-department-member-tile(
          v-for="member in department.members"
          :key="member['@id']"
        )
          UserAvatar._avatar(:user="member.employee.user" :size="40")
          ._info
            span._name(@click="openUserDetail(member)") {{ member.employee.user.name }} {{ member.employee.user.surname }}
            span._position {{ member.roleName }}
              span(v-if="member.employee.position") &nbsp| {{ member.employee.position }}
          EllipsisMenu._menu(:items="getMemberMenu(member)")
    ._footer(v-if="isOwner || isDeputy || isHead")
      Link(v-if="department.id === 0" :to="{ name: 'project-list', query: { departments: department.allDepartments.join(',') } }") Перейти к списку всех проектов компании
      Link(v-else :to="{ name: 'project-list', query: { departments: department.id } }") Перейти к списку проектов отдела
      Btn(dark @click="addMember") Добавить участника отдела
</template>

<style scoped lang="scss">

.department-tile {
  &__top {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 30px;
  }

  &__my {
    font-size: 12px;
    display: flex;
    align-items: center;
    text-align: center;
    letter-spacing: 0.03em;
    color: #FFFFFF;
    border-radius: 4px;
    padding: 0 14px;
    box-sizing: border-box;
    height: 20px;
    background: #5AC169;
  }

  &__menu {
    margin-left: auto;
  }

  &__body {
    padding-top: 26px;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 18px;
  }

  &__empty {
    grid-column: 1/-1;
  }

  &__footer {
    margin-top: 30px;
    display: flex;
    justify-content: flex-end;
    align-items: center;

    .button {
      margin-left: auto;
    }
  }

  @media (max-width: 1200px) {
    &__body {
      grid-template-columns: 1fr 1fr;
    }
  }

  @media (max-width: 768px) {
    &__body {
      grid-template-columns: 1fr;
    }

    &__footer {
      flex-direction: column;
      align-items: flex-start;
      gap: 30px;

      .button {
        margin-left: 0;
      }
    }
  }
}

.department-member-tile {
  display: grid;
  grid-template-columns: 40px 1fr 24px;
  column-gap: 20px;
  row-gap: 15px;

  background: #F8F9FC;
  padding: 15px 20px;
  border-radius: 6px;

  &__menu {
    display: none;
  }

  &:hover &__menu {
    display: block;
  }

  &__user {
    grid-column: 1 / 2 span;
  }

  &__info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 2px;
  }

  &__name {
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 0.01em;
    color: #1E3147;
    transition: color 0.3s;
    cursor: pointer;

    &:hover {
      color: var(--color-hover);
    }
  }

  &__position {
    font-weight: 400;
    font-size: 12px;
    line-height: 15px;
    letter-spacing: 0.01em;
    color: #788CA1;
  }
}

</style>
